import { useCallback, useLayoutEffect } from 'react';
import { gsap } from 'gsap';

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function useBelotArmenianAnimations({
  apply,
  play,
  stateRef,
  seatAnchorRefs,
  trickCenterRef,
  pendingPlayCmdRef,
  setCardFly,
  cardIdToPlaying,
  belotCardIsTrump,
  flyCardWrapRef,
  flyTweenRef,
  cardFly,
  cardPlayFlightSec,
}) {
  const runBotPlayCommand = useCallback((cmd, cur) => {
    const fromEl = seatAnchorRefs.current[cur.currentTurnSeat];
    const toEl = trickCenterRef.current;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (!fromEl || !toEl || reduced) {
      try {
        apply(cmd);
        const tr = cur.contract?.trumpSuit;
        const isTr =
          tr != null && cmd?.payload?.card != null && belotCardIsTrump(tr, cmd.payload.card);
        play(isTr ? 'belot-card-table-trump' : 'belot-card-table', 0.78);
      } catch (e) {
        console.error(e);
      }
      return;
    }
    const card = cmd.payload?.card;
    if (!Number.isInteger(card)) return;
    const { rank, suit } = cardIdToPlaying(card);
    const fr = fromEl.getBoundingClientRect();
    const tr = toEl.getBoundingClientRect();
    pendingPlayCmdRef.current = cmd;
    setCardFly({
      key: Date.now(),
      x0: fr.left + fr.width / 2,
      y0: fr.top + fr.height / 2,
      x1: tr.left + tr.width / 2,
      y1: tr.top + tr.height / 2,
      rank,
      suit,
      faceDown: true,
    });
  }, [seatAnchorRefs, trickCenterRef, apply, belotCardIsTrump, play, cardIdToPlaying, pendingPlayCmdRef, setCardFly]);

  useLayoutEffect(() => {
    if (!cardFly) return undefined;
    const el = flyCardWrapRef.current;
    const cmd = pendingPlayCmdRef.current;
    if (!el || !cmd) {
      pendingPlayCmdRef.current = null;
      queueMicrotask(() => setCardFly(null));
      return undefined;
    }
    flyTweenRef.current?.kill();
    const dx = cardFly.x1 - cardFly.x0;
    const dy = cardFly.y1 - cardFly.y0;
    const dist = Math.hypot(dx, dy);
    const fromDrag = Boolean(cardFly.fromDrag);
    const signX = dx === 0 ? 1 : Math.sign(dx);
    const arcLift = clamp(dist * 0.1, 28, 72);
    const c1x = cardFly.x0 + dx * 0.22;
    const c1y = cardFly.y0 - arcLift * 0.7;
    const c2x = cardFly.x0 + dx * 0.62;
    const c2y = cardFly.y0 + dy * 0.46 - arcLift;
    const anticipationDur = fromDrag ? clamp(cardPlayFlightSec * 0.18, 0.07, 0.1) : clamp(cardPlayFlightSec * 0.28, 0.1, 0.14);
    const flightDur = fromDrag ? clamp(cardPlayFlightSec * 0.56, 0.2, 0.32) : clamp(cardPlayFlightSec * 0.72, 0.26, 0.42);
    const skewPeak = clamp((dist / 500) * signX * 5, -7, 7);
    const impactShadow = fromDrag ? '0 10px 18px rgba(0, 0, 0, 0.32)' : '0 14px 24px rgba(0, 0, 0, 0.42)';
    const centerEl = trickCenterRef.current;
    gsap.set(el, {
      left: cardFly.x0,
      top: cardFly.y0,
      xPercent: -50,
      yPercent: -50,
      rotation: signX * -10,
      scale: 0.9,
      skewX: 0,
      boxShadow: '0 0 0 rgba(0,0,0,0)',
      opacity: 1,
    });
    const tl = gsap.timeline({
      onComplete: () => {
        const c = pendingPlayCmdRef.current;
        pendingPlayCmdRef.current = null;
        setCardFly(null);
        if (!c) return;
        const st = stateRef.current;
        const tr = st.contract?.trumpSuit;
        const card = c.payload?.card;
        const isTr = card != null && tr != null && belotCardIsTrump(tr, card);
        play('card-flip', 0.52);
        play(isTr ? 'belot-card-table-trump' : 'belot-card-table', 0.58);
        apply(c);
      },
    });
    tl.to(el, {
      duration: anticipationDur,
      ease: 'power1.out',
      y: fromDrag ? '-=6' : '-=10',
      scale: fromDrag ? 1.02 : 1.05,
      rotation: signX * (fromDrag ? 3 : 5),
      skewX: skewPeak * 0.4,
    });
    tl.to(el, {
      duration: flightDur,
      ease: 'power2.inOut',
      keyframes: [
        { left: c1x, top: c1y, rotation: signX * 8, skewX: skewPeak * 0.75, scale: 1.01 },
        { left: c2x, top: c2y, rotation: signX * 3, skewX: skewPeak, scale: 1 },
        { left: cardFly.x1, top: cardFly.y1, rotation: gsap.utils.random(-10, 10), skewX: skewPeak * 0.35, scale: 1 },
      ],
    });
    tl.to(
      el,
      {
        duration: 0.05,
        ease: 'power2.out',
        scale: fromDrag ? 1.01 : 1.02,
        boxShadow: impactShadow,
      },
      '>-0.02',
    );
    tl.to(el, {
      duration: 0.07,
      ease: 'power1.out',
      scale: 1,
      skewX: 0,
      boxShadow: '0 0 0 rgba(0,0,0,0)',
    });
    if (centerEl && !fromDrag) {
      tl.to(
        centerEl,
        {
          duration: 0.05,
          ease: 'power1.inOut',
          x: '+=1.4',
          y: '-=1.2',
          yoyo: true,
          repeat: 1,
        },
        '<',
      );
      tl.set(centerEl, { clearProps: 'x,y' });
    }
    flyTweenRef.current = tl;
    return () => {
      flyTweenRef.current?.kill();
      flyTweenRef.current = null;
    };
  }, [
    cardFly,
    flyCardWrapRef,
    pendingPlayCmdRef,
    flyTweenRef,
    stateRef,
    belotCardIsTrump,
    play,
    apply,
    setCardFly,
    cardPlayFlightSec,
    trickCenterRef,
  ]);

  return { runBotPlayCommand };
}

